import React, { useState, useEffect } from 'react';
import { Form, Card } from 'react-bootstrap';
import HTTP from '../../../config/axios';

interface Organization {
    id: number;
    name: string;
    type: string;
    type_id: number;
    parent_id: number;
    level: number;
}

interface HierarchicalOrgSelectorProps {
    onSelectionChange: (dealerships: number[]) => void;
    selectedDealership?: number[];
    disabled?: boolean;
    autoSelectAll?: boolean;
}

const HierarchicalOrgSelector: React.FC<HierarchicalOrgSelectorProps> = ({ onSelectionChange, selectedDealership = [], disabled = false, autoSelectAll = false }) => {
    const [dealerships, setDealerships] = useState<Organization[]>([]);
    const [outlets, setOutlets] = useState<Organization[]>([]);
    const [selectedDealerships, setSelectedDealerships] = useState<number[]>([]);
    


    useEffect(() => {
        fetchOrganizations();
    }, []);

    useEffect(() => {
        // Immediately update if we have a valid selection
        if (selectedDealership && selectedDealership.length > 0 && !selectedDealership.includes(0)) {
            setSelectedDealerships([...selectedDealership]); // Force new array reference
        } else if (selectedDealership && selectedDealership.length === 0) {
            setSelectedDealerships([]);
        }
    }, [selectedDealership, autoSelectAll]);

    // Auto-select all dealerships when autoSelectAll is true
    useEffect(() => {
        if (autoSelectAll && dealerships.length > 0) {
            const allDealershipIds = dealerships.map(d => d.id);
            setSelectedDealerships(allDealershipIds);
            onSelectionChange(allDealershipIds);
        }
    }, [autoSelectAll, dealerships]); // Removed onSelectionChange to prevent infinite loops

    const fetchOrganizations = async () => {    
        try {
            const response = await HTTP.get('/api/admin/user/organizations');
            const { data: { status, data } } = response;
            if (status === 1) {

                setDealerships(data.dealerships);
                setOutlets(data.outlets);
            }
        } catch (error) {
            console.error('Error fetching organizations:', error);
        }
    };

    const handleDealershipChange = (dealershipId: number, checked: boolean) => {
        if (disabled) {
            return;
        }
        
        let newSelected: number[];
        
        if (autoSelectAll) {
            // Management roles - checkbox behavior (shouldn't reach here as it's disabled)
            if (checked) {
                newSelected = [...selectedDealerships, dealershipId];
            } else {
                newSelected = selectedDealerships.filter(id => id !== dealershipId);
            }
        } else {
            // Non-management roles - single selection
            newSelected = [dealershipId]; // Always select the clicked one
        }
        
        setSelectedDealerships(newSelected);
        onSelectionChange(newSelected);
    };

    const getOutletsForDealership = (dealershipId: number) => {
        return outlets.filter(outlet => outlet.parent_id === dealershipId);
    };

    return (
        <Card className='mt-3'>
            <Card.Header className="bg-primary text-white">
                <h6>Organization Access {autoSelectAll && <small>(All Selected)</small>}</h6>
            </Card.Header>
            <Card.Body style={{ 
                maxHeight: '300px', 
                overflowY: 'auto',
                 opacity: disabled ? 0.6 : 1,
            }}>
                {dealerships.map(dealership => (
                    <div key={dealership.id} className="mb-3">
                        <Form.Check
                            type={autoSelectAll ? "checkbox" : "radio"}
                            name={autoSelectAll ? undefined : "dealership-selection"}
                            id={`dealership-${dealership.id}`}
                            label={` ${dealership.name}`}
                            checked={selectedDealerships.includes(dealership.id)}
                            value={dealership.id}
                            onChange={(e) => {
                                handleDealershipChange(dealership.id, e.target.checked);
                            }}
                            className="fw-bold"
                            disabled={disabled}
                        />
                        
                        <div className="ms-4 mt-2">
                            {getOutletsForDealership(dealership.id).map(outlet => (
                                <div key={outlet.id} className="mb-1 text-muted ms-3">
                                     {outlet.name}
                                </div>
                            ))}
                        </div>
                    </div>
                ))}
            </Card.Body>
        </Card>
    );
};

export default HierarchicalOrgSelector;